import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { debounce } from 'throttle-debounce';
import { linkedPanelWidthSelector, rtlEnable } from '@/domain/env';
import { documentLinkedOpenStatusSelector } from '@/domain/documents';
import { createMemoFn } from '@/lib/propHelpers';
import withState from '@/components/withState';
import { DnDItemSource, DOCUMENT } from '@/components/DnDItem';

import {
  Modal,
  ModalCloseButton,
  PreviewCounter,
  PreviewBadges,
  PreviewContent,
  PreviewNavigation,
  PreviewNavigationButton,
} from '@/pages/company/WorkspaceRoute/components/DocumentPreview/components/StyledComponents';
import PreviewActionGroup from '@/pages/company/WorkspaceRoute/components/DocumentPreview/components/PreviewActionGroup';
import LinkedBadge from '@/pages/company/WorkspaceRoute/components/DocumentPreview/components/LinkedBadge';
import PaidBadge from '@/pages/company/WorkspaceRoute/components/DocumentPreview/components/PaidBadge';
import PaymentInProgressBadge from '@/pages/company/WorkspaceRoute/components/DocumentPreview/components/PaymentInProgressBadge';
import CircularProgressWithBackdrop from '@/components/mui/CircularProgressWithBackdrop';
import RouterLink from '@/components/mui/Router/RouterLink';
import Box from '@mui/material/Box';

import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import NavigateNextOutlinedIcon from '@mui/icons-material/NavigateNextOutlined';
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';
import cx from 'classnames';

const DragSource = DnDItemSource(DOCUMENT);

const scaleSize = (rawSize, ratio) => {
  const { width, height } = rawSize;
  return { width: width * ratio, height: height * ratio };
};

class DocumentPreview extends React.Component {
  imageRulerEl = null;

  btnBoxEl = null;

  loadTimer = null;

  constructor() {
    super();
    this.state = {
      lastLoadedSrc: null,
      rawSize: { width: 1, height: 1 },
      isPaid: false,
      isSuppressSpinner: false,
      isPaymentInProgress: false,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.onWindowResize);
  }

  componentDidUpdate(prevProps) {
    const { isPaid, isPaymentInProgress, src } = this.props;
    if (prevProps.isPaid !== isPaid && !this.isLoading()) {
      this.onUpdatePaidStatus();
    }
    if (prevProps.isPaymentInProgress !== isPaymentInProgress && !this.isLoading()) {
      this.onUpdatePaymentInProgressStatus();
    }

    if (src !== prevProps.src) {
      this.onStartLoad();
    }
  }

  componentWillUnmount() {
    const { close } = this.props;
    this.clearTimer();
    window.removeEventListener('resize', this.onWindowResize);
    close();
  }

  onWindowResize = () => this.onInit();

  onUpdatePaidStatus = () => {
    const { isPaid } = this.props;
    this.setState({ isPaid });
  };

  onUpdatePaymentInProgressStatus = () => {
    const { isPaymentInProgress } = this.props;
    this.setState({ isPaymentInProgress });
  };

  onStartLoad = () => {
    const { isSuppressSpinner } = this.state;
    if (!isSuppressSpinner) {
      this.setState({ isSuppressSpinner: true });
      this.loadTimer = setTimeout(() => {
        this.clearTimer();
        this.setState({ isSuppressSpinner: false });
      }, 700);
    }
  };

  onInit = debounce(30, () => {
    const { src, rotationAngle, isPaid, isPaymentInProgress, setRotationAngleState } = this.props;
    this.setState({
      lastLoadedSrc: src,
      rawSize: this.getImgRawSize(),
      isPaid,
      isPaymentInProgress,
      isSuppressSpinner: false,
    });
    this.clearTimer();
    setRotationAngleState(rotationAngle);
  });

  onClickContextBtn = (e) => {
    e.stopPropagation();
    const { onContextMenu, isContextMenuOpen, isRtl } = this.props;

    if (onContextMenu && !isContextMenuOpen) {
      // TODO: rework :(
      const { x, y, width, height } = this.btnBoxEl.getBoundingClientRect();
      const rtlShift = isRtl ? width : 0;
      e.clientX = x + rtlShift;
      e.clientY = y + height + 5;
      onContextMenu(e);
    }
  };

  onClickPrev = (e) => {
    const { onPrev } = this.props;
    e.stopPropagation();
    onPrev();
  };

  onClickNext = (e) => {
    const { onNext } = this.props;
    e.stopPropagation();
    onNext();
  };

  getPaidStatus = () => {
    const { isPaid: isPaidState } = this.state;
    const { isPaid } = this.props;
    return this.isLoading() ? isPaidState : isPaid;
  };

  getPaymentInProgressStatus = () => {
    const { isPaymentInProgress: isPaymentInProgressState } = this.state;
    const { isPaymentInProgress } = this.props;
    return this.isLoading() ? isPaymentInProgressState : isPaymentInProgress;
  };

  getXSpace = () => {
    const { linkedPanelWidth, isOpenLinkedPanel, xGap = 0 } = this.props;
    const offset = isOpenLinkedPanel ? linkedPanelWidth : 0;

    return window.innerWidth - offset - xGap;
  };

  getAvailableSpace = createMemoFn(() => {
    const { yGap = 0 } = this.props;
    const { innerHeight } = window;
    return {
      height: innerHeight - 100 - yGap,
      width: this.getXSpace() - 300,
    };
  });

  getImgRawSize = createMemoFn(
    () => {
      const { lastLoadedSrc } = this.state;
      return {
        lastLoadedSrc,
        imageRulerEl: this.imageRulerEl,
        isLoading: this.isLoading(), // use as trigger because this.imageRulerEl never change
      };
    },
    ({ imageRulerEl }) => {
      const { width, height } = imageRulerEl ? imageRulerEl.getBoundingClientRect() : { width: 1, height: 1 };
      return { width, height };
    },
  );

  getPresentationImgSize = createMemoFn(
    () => {
      const { rotationAngleState } = this.props;
      const { rawSize } = this.state;
      return {
        rotationAngleState,
        rawSize,
      };
    },
    ({ rotationAngleState, rawSize }) => {
      const { width: w, height: h } = rawSize;
      // if doc rotated 90, 270, etc. Set width equal height
      const isShuffle = rotationAngleState ? !!((rotationAngleState / 90) % 2) : false;
      const [width, height] = isShuffle ? [h, w] : [w, h];
      return { width, height };
    },
  );

  getRatio = () => {
    const rawSize = this.getPresentationImgSize();
    const space = this.getAvailableSpace();
    const widthRatio = space.width / rawSize.width;
    const heightRatio = space.height / rawSize.height;
    return Math.min(widthRatio, heightRatio);
  };

  getContainerStyle = createMemoFn(() => ({ width: this.getXSpace() }));

  getBoxStyle = createMemoFn(
    () => ({ rawSize: this.getPresentationImgSize(), ratio: this.getRatio() }),
    ({ rawSize, ratio }) => scaleSize(rawSize, ratio),
  );

  getImageStyle = createMemoFn(
    () => {
      const { rawSize } = this.state;
      return { rawSize, ratio: this.getRatio() };
    },
    ({ rawSize, ratio }) => scaleSize(rawSize, ratio),
  );

  clearTimer = () => {
    if (this.loadTimer) {
      clearTimeout(this.loadTimer);
      this.loadTimer = null;
    }
  };

  isContextMenuAvailable = () => {
    const { onContextMenu } = this.props;
    return onContextMenu;
  };

  isLoading = () => {
    const { src, isLoading } = this.props;
    const { lastLoadedSrc } = this.state;
    return src !== lastLoadedSrc || isLoading;
  };

  isDisable = () => {
    const { isContextMenuOpen, disabled, isTrash } = this.props;
    return disabled || isContextMenuOpen || isTrash;
  };

  initializeImageProxy = (el) => {
    this.imageRulerEl = el;
  };

  refBtnBox = (el) => {
    this.btnBoxEl = el;
  };

  renderImagePresentation = createMemoFn(
    () => {
      const { lastLoadedSrc } = this.state;

      return {
        lastLoadedSrc,
        style: this.getImageStyle(),
      };
    },
    ({ lastLoadedSrc, style }) =>
      lastLoadedSrc ? (
        <img src={lastLoadedSrc} alt="document preview" draggable={false} ref={this.refProxy} style={style} />
      ) : (
        <div />
      ),
  );

  renderImage = createMemoFn(
    () => {
      const { isOpenLinkedPanel, documentID, to, onClick } = this.props;
      const image = this.renderImagePresentation();
      return { image, isOpenLinkedPanel, documentID, to, onClick, disabled: this.isDisable() };
    },
    ({ image, isOpenLinkedPanel, disabled, documentID, to, onClick }) => {
      if (disabled) {
        return <Box display="flex">{image}</Box>;
      }

      if (isOpenLinkedPanel) {
        return (
          <Box display="flex" component={DragSource} isDragging id={documentID}>
            {image}
          </Box>
        );
      }

      return (
        <Box display="flex" component={RouterLink} href={to} onClick={onClick}>
          {image}
        </Box>
      );
    },
  );

  render() {
    const {
      onClick,
      to,
      disabled,
      onPrev,
      onNext,
      isDisablePrev,
      isDisableNext,
      src,
      close,
      isContextMenuOpen,
      currentPage,
      allPagesCount,
      isLinked = false,
      isOpenLinkedPanel,
      linkedPanelWidth,
      xGap,
      rotationAngleState,
      isRtl,
      isTrash,
    } = this.props;

    const { isSuppressSpinner } = this.state;

    const isPaid = this.getPaidStatus();
    const isPaymentInProgress = this.getPaymentInProgressStatus();
    const isBusy = !isSuppressSpinner && this.isLoading();

    return (
      <Modal open onClose={close} offset={isOpenLinkedPanel ? linkedPanelWidth : xGap || 0}>
        <>
          {/* for load image and calculate size etc, onLoad={this.onInit} */}
          <Box zIndex={-1} position="absolute" height={0} width={0} overflow="hidden">
            <img
              src={src}
              alt="document preview"
              onLoad={this.onInit}
              ref={this.initializeImageProxy} // TODO: rework :(
            />
          </Box>

          {isBusy && <CircularProgressWithBackdrop />}

          {onPrev && (
            <PreviewNavigation>
              <PreviewNavigationButton onClick={this.onClickPrev} disabled={isDisablePrev}>
                {isRtl ? <NavigateNextOutlinedIcon /> : <NavigateBeforeOutlinedIcon />}
              </PreviewNavigationButton>
            </PreviewNavigation>
          )}

          <PreviewContent
            className={cx({
              'PreviewContent-isPaid': isPaid,
              'PreviewContent-isLinked': isLinked,
              'PreviewContent-isPaymentInProgress': isPaymentInProgress && !isPaid,
            })}
            style={{ ...this.getBoxStyle() }}
          >
            <ModalCloseButton onClick={close}>
              <CloseOutlinedIcon />
            </ModalCloseButton>

            <Box boxShadow={10} sx={{ transform: `rotate(${rotationAngleState}deg)` }}>
              {this.renderImage()}
            </Box>

            {allPagesCount && <PreviewCounter>{`${currentPage} / ${allPagesCount}`}</PreviewCounter>}

            {!disabled && !isTrash && (
              <PreviewActionGroup
                href={to}
                onClickLink={onClick}
                onClickContextMenu={this.onClickContextBtn}
                withContext={this.isContextMenuAvailable()}
                isContextMenuOpen={isContextMenuOpen}
                contextMenuRef={this.refBtnBox}
              />
            )}

            {(isLinked || isPaid || isPaymentInProgress) && (
              <PreviewBadges>
                {isPaid && <PaidBadge />}
                {isPaymentInProgress && !isPaid && <PaymentInProgressBadge />}
                {isLinked && <LinkedBadge />}
              </PreviewBadges>
            )}
          </PreviewContent>

          {onNext && (
            <PreviewNavigation>
              <PreviewNavigationButton onClick={this.onClickNext} disabled={isDisableNext}>
                {isRtl ? <NavigateBeforeOutlinedIcon /> : <NavigateNextOutlinedIcon />}
              </PreviewNavigationButton>
            </PreviewNavigation>
          )}
        </>
      </Modal>
    );
  }
}

const mapStateToProps = (state) => ({
  linkedPanelWidth: linkedPanelWidthSelector(state),
  isOpenLinkedPanel: documentLinkedOpenStatusSelector(state),
  isRtl: rtlEnable(state),
});

export default compose(
  withState('rotationAngleState', 'setRotationAngleState', 0),
  connect(mapStateToProps),
)(DocumentPreview);
